import { useEffect, useRef } from "react";
import { useObserverStore } from "../stores/observerStore";
import type { AgentState, TravelState } from "../stores/observerStore";

interface SnapshotEventData {
	id: number;
	eventType: string;
	good: string;
	zoneMin: number;
	zoneMax: number;
	priceMultiplier: number;
	endsAt: number;
	description: string;
}

interface SnapshotData {
	agents: AgentState[];
	travels: TravelState[];
	galacticEvents?: SnapshotEventData[];
}

interface AgentRegisteredData {
	agentId: string;
	name: string;
	planetId: string;
}

interface TradeData {
	agentId: string;
	agentName: string;
	planetId: string;
	good: string;
	action: "buy" | "sell";
	quantity: number;
	unitPrice: number;
	timestamp: number;
}

interface TravelStartedData {
	agentId: string;
	agentName: string;
	fromPlanetId: string;
	toPlanetId: string;
	departedAt: number;
	arrivesAt: number;
}

interface TravelArrivedData {
	agentId: string;
	agentName: string;
	planetId: string;
}

interface GalacticEventData {
	eventId?: number;
	eventType: string;
	good: string;
	zoneMin: number;
	zoneMax: number;
	multiplier: number;
	action: "started" | "expired";
	description: string;
	endsAt: number;
	timestamp: number;
}

export function useAgentSSE(): void {
	const reconnectRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => {
		let es: EventSource | null = null;
		let mounted = true;

		function connect() {
			const url = `${window.location.origin}/observe/events`;
			es = new EventSource(url);

			es.addEventListener("snapshot", (e) => {
				const data = JSON.parse(e.data) as SnapshotData;
				const galEvts = (data.galacticEvents ?? []).map((evt) => ({
					id: String(evt.id),
					eventType: evt.eventType,
					good: evt.good,
					zoneMin: evt.zoneMin,
					zoneMax: evt.zoneMax,
					multiplier: evt.priceMultiplier,
					description: evt.description,
					expiresAt: evt.endsAt,
				}));
				useObserverStore.getState().applySnapshot(data.agents, data.travels, galEvts);
			});

			es.addEventListener("agent-registered", (e) => {
				const data = JSON.parse(e.data) as AgentRegisteredData;
				useObserverStore.getState().addAgent({
					id: data.agentId,
					name: data.name,
					credits: 1000,
					locationPlanetId: data.planetId,
				});
				useObserverStore.getState().addEvent({
					type: "agent-registered",
					message: `${data.name} registered at ${data.planetId}`,
					timestamp: Date.now(),
				});
			});

			es.addEventListener("trade", (e) => {
				const data = JSON.parse(e.data) as TradeData;
				useObserverStore.getState().addTrade({
					...data,
					id: crypto.randomUUID(),
					fadeOut: Date.now() + 5000,
				});
				useObserverStore.getState().addEvent({
					type: "trade",
					message: `${data.agentName} ${data.action === "buy" ? "bought" : "sold"} ${data.quantity} ${data.good}`,
					timestamp: data.timestamp,
				});
			});

			es.addEventListener("travel-started", (e) => {
				const data = JSON.parse(e.data) as TravelStartedData;
				useObserverStore.getState().addTravel(data);
				useObserverStore.getState().addEvent({
					type: "travel-started",
					message: `${data.agentName} departed ${data.fromPlanetId} → ${data.toPlanetId}`,
					timestamp: Date.now(),
				});
			});

			es.addEventListener("travel-arrived", (e) => {
				const data = JSON.parse(e.data) as TravelArrivedData;
				useObserverStore.getState().removeTravel(data.agentId);
				useObserverStore.getState().updateAgentLocation(data.agentId, data.planetId);
				useObserverStore.getState().addEvent({
					type: "travel-arrived",
					message: `${data.agentName || data.agentId.slice(0, 8)} arrived at ${data.planetId}`,
					timestamp: Date.now(),
				});
			});

			es.addEventListener("galactic-event", (e) => {
				const data = JSON.parse(e.data) as GalacticEventData;
				if (data.action === "started") {
					useObserverStore.getState().addGalacticEvent({
						id: data.eventId != null ? String(data.eventId) : crypto.randomUUID(),
						eventType: data.eventType,
						good: data.good,
						zoneMin: data.zoneMin,
						zoneMax: data.zoneMax,
						multiplier: data.multiplier,
						description: data.description,
						expiresAt: data.endsAt,
					});
				}
				const label = data.action === "started" ? "STARTED" : "EXPIRED";
				useObserverStore.getState().addEvent({
					type: "galactic-event",
					message: `[${label}] ${data.description}`,
					timestamp: data.timestamp,
				});
			});

			es.onopen = () => useObserverStore.getState().setConnected(true);
			es.onerror = () => {
				useObserverStore.getState().setConnected(false);
				es?.close();
				if (mounted) {
					reconnectRef.current = setTimeout(connect, 3000);
				}
			};
		}

		connect();

		return () => {
			mounted = false;
			es?.close();
			clearTimeout(reconnectRef.current);
		};
	}, []);
}
